import API_BASE_URL from '../config/api';

const authHeaders = () => {
  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Simulate API delay
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Generate comprehensive analytics data
const generateAnalyticsData = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Generate monthly data for the past 12 months
  const monthlyData = [];
  for (let i = 11; i >= 0; i--) {
    const date = new Date(currentYear, currentMonth - i, 1);
    const monthName = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();

    // Generate realistic data with some randomness
    const baseRevenue = 50000 + Math.random() * 30000;
    const baseOrders = 150 + Math.random() * 100;
    const baseCustomers = 80 + Math.random() * 50;
    const baseProducts = 25 + Math.random() * 15;

    monthlyData.push({
      month: monthName,
      year: year,
      revenue: Math.round(baseRevenue),
      orders: Math.round(baseOrders),
      customers: Math.round(baseCustomers),
      products: Math.round(baseProducts),
      averageOrderValue: Math.round(baseRevenue / baseOrders),
      conversionRate: 2.5 + Math.random() * 1.5,
      returnRate: 0.5 + Math.random() * 1.0,
    });
  }

  // Generate daily data for the last 30 days
  const dailyData = [];
  for (let i = 29; i >= 0; i--) {
    const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
    const day = date.getDate();

    // Weekend effect
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const weekendMultiplier = isWeekend ? 0.6 : 1;

    const baseRevenue = (2000 + Math.random() * 1500) * weekendMultiplier;
    const baseOrders = (8 + Math.random() * 12) * weekendMultiplier;

    dailyData.push({
      date: date.toISOString().split('T')[0],
      day: day,
      dayName: dayName,
      revenue: Math.round(baseRevenue),
      orders: Math.round(baseOrders),
      customers: Math.round(baseOrders * 0.8),
      averageOrderValue: Math.round(baseRevenue / baseOrders),
    });
  }

  // Generate hourly data for the last 24 hours
  const hourlyData = [];
  for (let i = 23; i >= 0; i--) {
    const hour = (now.getHours() - i + 24) % 24;
    const isBusinessHours = hour >= 9 && hour <= 17;
    const businessMultiplier = isBusinessHours ? 1.5 : 0.3;

    const baseRevenue = (100 + Math.random() * 200) * businessMultiplier;
    const baseOrders = (1 + Math.random() * 3) * businessMultiplier;

    hourlyData.push({
      hour: hour,
      time: `${hour.toString().padStart(2, '0')}:00`,
      revenue: Math.round(baseRevenue),
      orders: Math.round(baseOrders),
      customers: Math.round(baseOrders * 0.9),
    });
  }

  // Generate product performance data
  const productCategories = [
    'Paints',
    'Workwear',
    'Safety Equipment',
    'Tools',
    'Accessories',
  ];

  const productData = productCategories.map((category) => {
    const baseRevenue = 10000 + Math.random() * 20000;
    const baseOrders = 30 + Math.random() * 50;
    const baseQuantity = 100 + Math.random() * 200;

    return {
      category,
      revenue: Math.round(baseRevenue),
      orders: Math.round(baseOrders),
      quantity: Math.round(baseQuantity),
      averagePrice: Math.round(baseRevenue / baseQuantity),
      growth: -20 + Math.random() * 40, // -20% to +20%
    };
  });

  // Generate customer acquisition data
  const acquisitionChannels = [
    'Organic Search',
    'Paid Search',
    'Social Media',
    'Email',
    'Direct',
    'Referral',
  ];

  const acquisitionData = acquisitionChannels.map((channel) => {
    const baseCustomers = 20 + Math.random() * 80;
    const baseRevenue = baseCustomers * (200 + Math.random() * 300);
    const baseCost = baseRevenue * (0.1 + Math.random() * 0.3);

    return {
      channel,
      customers: Math.round(baseCustomers),
      revenue: Math.round(baseRevenue),
      cost: Math.round(baseCost),
      roi: Math.round(((baseRevenue - baseCost) / baseCost) * 100),
      conversionRate: 1 + Math.random() * 4,
    };
  });

  // Generate geographic data
  const countries = [
    'United States',
    'Canada',
    'United Kingdom',
    'Germany',
    'France',
    'Australia',
    'Japan',
    'Brazil',
    'India',
    'Mexico',
  ];

  const geographicData = countries.map((country) => {
    const baseRevenue = 5000 + Math.random() * 15000;
    const baseOrders = 15 + Math.random() * 35;
    const baseCustomers = 10 + Math.random() * 25;

    return {
      country,
      revenue: Math.round(baseRevenue),
      orders: Math.round(baseOrders),
      customers: Math.round(baseCustomers),
      averageOrderValue: Math.round(baseRevenue / baseOrders),
    };
  });

  // Generate order status distribution
  const orderStatusData = [
    { status: 'Pending', count: 25, percentage: 12.5 },
    { status: 'Processing', count: 45, percentage: 22.5 },
    { status: 'Shipped', count: 80, percentage: 40.0 },
    { status: 'Delivered', count: 45, percentage: 22.5 },
    { status: 'Cancelled', count: 5, percentage: 2.5 },
  ];

  // Generate payment method distribution
  const paymentMethodData = [
    { method: 'Credit Card', count: 120, percentage: 60.0 },
    { method: 'PayPal', count: 45, percentage: 22.5 },
    { method: 'Bank Transfer', count: 25, percentage: 12.5 },
    { method: 'Other', count: 10, percentage: 5.0 },
  ];

  // Generate top products data
  const topProducts = [
    {
      name: 'Professional Paint Brush Set',
      revenue: 15420,
      orders: 85,
      growth: 15.2,
    },
    { name: 'Safety Work Jacket', revenue: 12850, orders: 65, growth: 8.7 },
    { name: 'Industrial Primer', revenue: 11200, orders: 70, growth: -2.1 },
    { name: 'Work Boots', revenue: 9800, orders: 45, growth: 22.3 },
    { name: 'Safety Helmet', revenue: 8750, orders: 55, growth: 5.8 },
  ];

  // Calculate summary metrics
  const currentMonthData = monthlyData[monthlyData.length - 1];
  const previousMonthData = monthlyData[monthlyData.length - 2];

  const totalRevenue = monthlyData.reduce(
    (sum, month) => sum + month.revenue,
    0
  );
  const totalOrders = monthlyData.reduce((sum, month) => sum + month.orders, 0);
  const totalCustomers = monthlyData.reduce(
    (sum, month) => sum + month.customers,
    0
  );

  const revenueGrowth = previousMonthData
    ? ((currentMonthData.revenue - previousMonthData.revenue) /
        previousMonthData.revenue) *
      100
    : 0;
  const ordersGrowth = previousMonthData
    ? ((currentMonthData.orders - previousMonthData.orders) /
        previousMonthData.orders) *
      100
    : 0;
  const customersGrowth = previousMonthData
    ? ((currentMonthData.customers - previousMonthData.customers) /
        previousMonthData.customers) *
      100
    : 0;

  return {
    summary: {
      totalRevenue: Math.round(totalRevenue),
      totalOrders: Math.round(totalOrders),
      totalCustomers: Math.round(totalCustomers),
      averageOrderValue: Math.round(totalRevenue / totalOrders),
      revenueGrowth: Math.round(revenueGrowth * 100) / 100,
      ordersGrowth: Math.round(ordersGrowth * 100) / 100,
      customersGrowth: Math.round(customersGrowth * 100) / 100,
    },
    monthlyData,
    dailyData,
    hourlyData,
    productData,
    acquisitionData,
    geographicData,
    orderStatusData,
    paymentMethodData,
    topProducts,
  };
};

const analyticsData = generateAnalyticsData();

// GET /api/analytics/summary – get key metrics summary
export const getAnalyticsSummary = async (timeRange = '30d') => {
  try {
    // Prefer new analytics summary endpoint
    const res = await fetch(
      `${API_BASE_URL}/analytics/summary?period=${encodeURIComponent(timeRange)}`,
      { headers: { ...authHeaders() } }
    );
    const json = await res.json();
    if (!res.ok || json?.success === false)
      throw new Error(json?.message || 'Failed');
    const s = json?.data?.summary || {};
    return {
      totalRevenue: s.totalRevenue ?? analyticsData.summary.totalRevenue,
      totalOrders: s.totalOrders ?? analyticsData.summary.totalOrders,
      totalCustomers: s.totalCustomers ?? analyticsData.summary.totalCustomers,
      averageOrderValue:
        s.averageOrderValue ?? analyticsData.summary.averageOrderValue,
      revenueGrowth: s.revenueGrowth ?? analyticsData.summary.revenueGrowth,
      ordersGrowth: s.orderGrowth ?? analyticsData.summary.ordersGrowth,
      customersGrowth:
        s.customerGrowth ?? analyticsData.summary.customersGrowth,
      timeRange,
      lastUpdated: new Date().toISOString(),
    };
  } catch (_) {
    // Fallback to admin dashboard endpoint if present
    try {
      const res = await fetch(`${API_BASE_URL}/admin/dashboard`, {
        headers: { ...authHeaders() },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed');
      const d = json?.data || {};
      return {
        totalRevenue: d.totalRevenue ?? analyticsData.summary.totalRevenue,
        totalOrders: d.totalOrders ?? analyticsData.summary.totalOrders,
        totalCustomers:
          d.totalCustomers ?? analyticsData.summary.totalCustomers,
        averageOrderValue:
          d.averageOrderValue ?? analyticsData.summary.averageOrderValue,
        revenueGrowth: d.revenueGrowth ?? analyticsData.summary.revenueGrowth,
        ordersGrowth: d.ordersGrowth ?? analyticsData.summary.ordersGrowth,
        customersGrowth:
          d.customersGrowth ?? analyticsData.summary.customersGrowth,
        timeRange,
        lastUpdated: new Date().toISOString(),
      };
    } catch (_) {
      // Final fallback to mock
      await delay(500);
      return {
        ...analyticsData.summary,
        timeRange,
        lastUpdated: new Date().toISOString(),
      };
    }
  }
};

// GET /api/analytics/revenue – get revenue analytics
export const getRevenueAnalytics = async (
  timeRange = '30d',
  granularity = 'daily'
) => {
  try {
    const res = await fetch(
      `${API_BASE_URL}/analytics/revenue?period=${encodeURIComponent(timeRange)}&granularity=${encodeURIComponent(granularity)}`,
      { headers: { ...authHeaders() } }
    );
    const json = await res.json();
    if (!res.ok || json?.success === false)
      throw new Error(json?.message || 'Failed');
    const revenueData = (json?.data?.revenueData || []).map((d) => {
      const id = d._id || '';
      const dt = id ? new Date(id.replace(' ', 'T')) : null;
      if (granularity === 'monthly' && id) {
        const [y, m] = id.split('-');
        const date = new Date(Number(y), Number(m) - 1, 1);
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return {
          month,
          year,
          label: `${month} ${year}`,
          revenue: d.revenue,
          orders: d.orders,
        };
      }
      if (granularity === 'daily' && dt && !isNaN(dt.getTime())) {
        const dayName = dt.toLocaleString('en-US', { weekday: 'short' });
        const day = dt.getDate();
        return {
          dayName,
          day,
          date: id,
          label: `${dayName} ${day}`,
          revenue: d.revenue,
          orders: d.orders,
        };
      }
      if (granularity === 'hourly' && id) {
        // id like YYYY-MM-DD HH:00:00
        const time = id.slice(11, 16);
        return { time, label: time, revenue: d.revenue, orders: d.orders };
      }
      return { date: id, label: id, revenue: d.revenue, orders: d.orders };
    });
    return {
      data: revenueData,
      granularity,
      timeRange,
      totalRevenue: revenueData.reduce(
        (sum, item) => sum + (item.revenue || 0),
        0
      ),
      averageRevenue: revenueData.length
        ? Math.round(
            revenueData.reduce((s, i) => s + (i.revenue || 0), 0) /
              revenueData.length
          )
        : 0,
    };
  } catch (_) {
    await delay(600);
    let data = [];
    switch (granularity) {
      case 'hourly':
        data = analyticsData.hourlyData;
        break;
      case 'daily':
        data = analyticsData.dailyData;
        break;
      case 'monthly':
        data = analyticsData.monthlyData;
        break;
      default:
        data = analyticsData.dailyData;
    }
    return {
      data,
      granularity,
      timeRange,
      totalRevenue: data.reduce((sum, item) => sum + item.revenue, 0),
      averageRevenue: Math.round(
        data.reduce((sum, item) => sum + item.revenue, 0) / data.length
      ),
    };
  }
};

// GET /api/analytics/orders – get orders analytics
export const getOrdersAnalytics = async (
  timeRange = '30d',
  granularity = 'daily'
) => {
  try {
    const res = await fetch(
      `${API_BASE_URL}/analytics/orders?period=${encodeURIComponent(timeRange)}`,
      { headers: { ...authHeaders() } }
    );
    const json = await res.json();
    if (!res.ok || json?.success === false)
      throw new Error(json?.message || 'Failed');
    const day = (json?.data?.ordersByDay || []).map((d) => {
      const id = d._id || '';
      const dt = id ? new Date(id) : null;
      if (granularity === 'monthly' && id.includes('-')) {
        const [y, m] = id.split('-');
        const date = new Date(Number(y), Number(m) - 1, 1);
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return {
          month,
          year,
          label: `${month} ${year}`,
          orders: d.count,
          revenue: d.revenue,
        };
      }
      if (dt && !isNaN(dt.getTime())) {
        const dayName = dt.toLocaleString('en-US', { weekday: 'short' });
        const dayNum = dt.getDate();
        return {
          dayName,
          day: dayNum,
          date: id,
          label: `${dayName} ${dayNum}`,
          orders: d.count,
          revenue: d.revenue,
        };
      }
      return { date: id, label: id, orders: d.count, revenue: d.revenue };
    });
    return {
      data: day,
      granularity,
      timeRange,
      totalOrders: day.reduce((sum, item) => sum + (item.orders || 0), 0),
      averageOrders: day.length
        ? Math.round(day.reduce((s, i) => s + (i.orders || 0), 0) / day.length)
        : 0,
    };
  } catch (_) {
    await delay(600);
    let data = [];
    switch (granularity) {
      case 'hourly':
        data = analyticsData.hourlyData;
        break;
      case 'daily':
        data = analyticsData.dailyData;
        break;
      case 'monthly':
        data = analyticsData.monthlyData;
        break;
      default:
        data = analyticsData.dailyData;
    }
    return {
      data,
      granularity,
      timeRange,
      totalOrders: data.reduce((sum, item) => sum + item.orders, 0),
      averageOrders: Math.round(
        data.reduce((sum, item) => sum + item.orders, 0) / data.length
      ),
    };
  }
};

// GET /api/analytics/customers – get customers analytics
export const getCustomersAnalytics = async (timeRange = '30d') => {
  await delay(600);

  return {
    data: analyticsData.monthlyData,
    timeRange,
    totalCustomers: analyticsData.summary.totalCustomers,
    newCustomers:
      analyticsData.monthlyData[analyticsData.monthlyData.length - 1].customers,
    returningCustomers: Math.round(analyticsData.summary.totalCustomers * 0.3),
  };
};

// GET /api/analytics/products – get product performance
export const getProductAnalytics = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/analytics/products`, {
      headers: { ...authHeaders() },
    });
    const json = await res.json();
    if (!res.ok || json?.success === false)
      throw new Error(json?.message || 'Failed');
    const d = json?.data || {};
    const categories = (d.productsByCategory || []).map((c) => ({
      category: c._id || 'Unknown',
      revenue: 0,
      orders: 0,
      quantity: c.count || 0,
      averagePrice: 0,
      growth: 0,
    }));
    return {
      categories: categories.length ? categories : analyticsData.productData,
      topProducts: d.topSellingProducts || analyticsData.topProducts,
      productsByStatus: d.productsByStatus || [],
      totalProducts: categories.reduce(
        (sum, cat) => sum + (cat.quantity || 0),
        0
      ),
      averagePrice: categories.length
        ? Math.round(
            categories.reduce((s, c) => s + (c.averagePrice || 0), 0) /
              categories.length
          )
        : Math.round(
            analyticsData.productData.reduce(
              (sum, cat) => sum + cat.averagePrice,
              0
            ) / analyticsData.productData.length
          ),
    };
  } catch (_) {
    await delay(500);
    return {
      categories: analyticsData.productData,
      topProducts: analyticsData.topProducts,
      productsByStatus: [
        { _id: 'active', count: 82 },
        { _id: 'low_stock', count: 12 },
        { _id: 'out_of_stock', count: 6 },
      ],
      totalProducts: analyticsData.productData.reduce(
        (sum, cat) => sum + cat.quantity,
        0
      ),
      averagePrice: Math.round(
        analyticsData.productData.reduce(
          (sum, cat) => sum + cat.averagePrice,
          0
        ) / analyticsData.productData.length
      ),
    };
  }
};

// GET /api/analytics/acquisition – get customer acquisition data
export const getAcquisitionAnalytics = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/analytics/acquisition`, {
      headers: { ...authHeaders() },
    });
    const json = await res.json();
    if (!res.ok || json?.success === false)
      throw new Error(json?.message || 'Failed');
    const d = json?.data || {};
    const channels = (d.acquisitionBySource || []).map((s) => ({
      channel: s._id || 'Direct',
      customers: s.count || 0,
      revenue: 0,
      cost: 0,
      roi: 0,
      conversionRate: 0,
    }));
    return {
      channels: channels.length ? channels : analyticsData.acquisitionData,
      totalCustomers: channels.reduce((sum, c) => sum + (c.customers || 0), 0),
      totalRevenue: channels.reduce((sum, c) => sum + (c.revenue || 0), 0),
      totalCost: channels.reduce((sum, c) => sum + (c.cost || 0), 0),
      averageROI: channels.length
        ? Math.round(
            channels.reduce((s, c) => s + (c.roi || 0), 0) / channels.length
          )
        : Math.round(
            analyticsData.acquisitionData.reduce(
              (sum, channel) => sum + channel.roi,
              0
            ) / analyticsData.acquisitionData.length
          ),
    };
  } catch (_) {
    await delay(500);
    return {
      channels: analyticsData.acquisitionData,
      totalCustomers: analyticsData.acquisitionData.reduce(
        (sum, channel) => sum + channel.customers,
        0
      ),
      totalRevenue: analyticsData.acquisitionData.reduce(
        (sum, channel) => sum + channel.revenue,
        0
      ),
      totalCost: analyticsData.acquisitionData.reduce(
        (sum, channel) => sum + channel.cost,
        0
      ),
      averageROI: Math.round(
        analyticsData.acquisitionData.reduce(
          (sum, channel) => sum + channel.roi,
          0
        ) / analyticsData.acquisitionData.length
      ),
    };
  }
};

// GET /api/analytics/geographic – get geographic distribution
export const getGeographicAnalytics = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/analytics/geographic`, {
      headers: { ...authHeaders() },
    });
    const json = await res.json();
    if (!res.ok || json?.success === false)
      throw new Error(json?.message || 'Failed');
    const d = json?.data || {};
    const byCountry = {};
    (d.ordersByCountry || []).forEach((o) => {
      const k = o._id || 'Unknown';
      byCountry[k] = {
        country: k,
        orders: o.count || 0,
        customers: 0,
        revenue: 0,
      };
    });
    (d.customersByCountry || []).forEach((c) => {
      const k = c._id || 'Unknown';
      byCountry[k] = {
        ...(byCountry[k] || { country: k, orders: 0, revenue: 0 }),
        customers: c.count || 0,
      };
    });
    (d.revenueByCountry || []).forEach((r) => {
      const k = r._id || 'Unknown';
      byCountry[k] = {
        ...(byCountry[k] || { country: k, orders: 0, customers: 0 }),
        revenue: r.revenue || 0,
      };
    });
    const countries = Object.values(byCountry);
    return {
      countries: countries.length ? countries : analyticsData.geographicData,
      totalRevenue: countries.reduce((sum, c) => sum + (c.revenue || 0), 0),
      totalOrders: countries.reduce((sum, c) => sum + (c.orders || 0), 0),
      totalCustomers: countries.reduce((sum, c) => sum + (c.customers || 0), 0),
    };
  } catch (_) {
    await delay(500);
    return {
      countries: analyticsData.geographicData,
      totalRevenue: analyticsData.geographicData.reduce(
        (sum, country) => sum + country.revenue,
        0
      ),
      totalOrders: analyticsData.geographicData.reduce(
        (sum, country) => sum + country.orders,
        0
      ),
      totalCustomers: analyticsData.geographicData.reduce(
        (sum, country) => sum + country.customers,
        0
      ),
    };
  }
};

// GET /api/analytics/orders/status – get order status distribution
export const getOrderStatusAnalytics = async () => {
  await delay(300);

  return {
    statuses: analyticsData.orderStatusData,
    totalOrders: analyticsData.orderStatusData.reduce(
      (sum, status) => sum + status.count,
      0
    ),
  };
};

// GET /api/analytics/payments – get payment method distribution
export const getPaymentAnalytics = async () => {
  await delay(300);

  return {
    methods: analyticsData.paymentMethodData,
    totalOrders: analyticsData.paymentMethodData.reduce(
      (sum, method) => sum + method.count,
      0
    ),
  };
};

// GET /api/analytics/export – export analytics data
export const exportAnalyticsData = async (
  format = 'csv',
  timeRange = '30d'
) => {
  await delay(1000);

  // Simulate file download
  const data = {
    summary: analyticsData.summary,
    monthlyData: analyticsData.monthlyData,
    dailyData: analyticsData.dailyData,
    productData: analyticsData.productData,
    acquisitionData: analyticsData.acquisitionData,
    geographicData: analyticsData.geographicData,
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);

  return {
    url,
    filename: `analytics-${timeRange}-${new Date().toISOString().split('T')[0]}.${format}`,
    size: blob.size,
  };
};

// GET /api/analytics/real-time – get real-time metrics
export const getRealTimeAnalytics = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/analytics/realtime`, {
      headers: { ...authHeaders() },
    });
    const json = await res.json();
    if (!res.ok || json?.success === false)
      throw new Error(json?.message || 'Failed');
    const d = json?.data || {};
    const last = d.lastHour || {};
    const nowIso = d.timestamp || new Date().toISOString();
    const hour = new Date(nowIso).getHours();
    return {
      currentHour: hour,
      revenue: last.revenue || 0,
      orders: last.orders || 0,
      customers: d.last24Hours?.newCustomers || 0,
      timestamp: nowIso,
      isBusinessHours: hour >= 9 && hour <= 17,
    };
  } catch (_) {
    await delay(200);
    const now = new Date();
    const currentHour = now.getHours();
    const currentData =
      analyticsData.hourlyData.find((h) => h.hour === currentHour) ||
      analyticsData.hourlyData[0];
    return {
      currentHour: currentData.hour,
      revenue: currentData.revenue,
      orders: currentData.orders,
      customers: currentData.customers,
      timestamp: now.toISOString(),
      isBusinessHours: currentHour >= 9 && currentHour <= 17,
    };
  }
};
